import { createHookContext } from '@clover-util/react-context';
import { useCallback, useEffect, useState } from 'react';

import { fetchPromotionDetails, Promotion } from 'services/shop/cart';
import { readCookie } from 'utils/Cookies';

import type { IDetailedPromotion } from './useProgramPromotion';
import { useReseller } from 'hooks/Reseller/useReseller';
import { useCartContext } from 'components/Cart/hooks/useCartContext';

export const { useValidatedProgramPromotion, ValidatedProgramPromotionProvider } = createHookContext(
  'ValidatedProgramPromotion',
  () => {
    const { resellerId } = useReseller();
    const {
      removePromo,
      cart: { products, cartMetadata },
    } = useCartContext();
    const [promotionDetails, setPromotionDetails] = useState<Promotion | undefined>();
    const [validatedProgramPromotion, setValidatedProgramPromotion] = useState<IDetailedPromotion>();
    const [isProgramPromoLoading, setIsProgramPromoLoading] = useState(false);
    const [cartFdmpIds, setCartFdmpIds] = useState<string[]>([]);

    const promotionCode = readCookie('program');

    const cacheProgramDetails = useCallback(async (promoCode: string, currResellerId: string) => {
      setIsProgramPromoLoading(true);
      try {
        const { data } = await fetchPromotionDetails({ resellerId: currResellerId, promoCode });
        setPromotionDetails(data);
        setIsProgramPromoLoading(false);
      } catch (e) {
        setIsProgramPromoLoading(false);
        console.error(`Error Fetching Program Promotion Details for ${currResellerId}, code ${promoCode}`);
      }
    }, []);

    useEffect(() => {
      if (!promotionCode || !resellerId) return;

      cacheProgramDetails(promotionCode, resellerId);
    }, [promotionCode, resellerId, cacheProgramDetails]);

    useEffect(() => {
      const allFdmpIdsInCart = [
        ...products.map(p => p.fdmpId),
        ...(cartMetadata?.hardware?.map(h => h.fdmpId) || []),
      ];
      setCartFdmpIds(allFdmpIdsInCart);
    }, [products, cartMetadata]);

    useEffect(() => {
      if (cartFdmpIds.length === 0) {
        setValidatedProgramPromotion(undefined);
        return;
      }

      const applicableFdmpIds = promotionDetails?.applicableToProducts.map(p => p.productId) || [];
      const validCart = cartFdmpIds.some(cartFdmpId => applicableFdmpIds.includes(cartFdmpId));

      if (validCart) {
        setValidatedProgramPromotion(promotionDetails);
        return;
      }

      removePromo();
      setValidatedProgramPromotion(undefined);
    }, [promotionDetails, cartFdmpIds]);

    return { programPromotion: validatedProgramPromotion, isProgramPromoLoading };
  },
);
